import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import FillInTheBlankDemo from '../../components/FillInTheBlankDemo';
import { fillInTheBlankDemoData } from '../../utils/mockData';
import {
  BookOpen,
  CheckCircle,
  ArrowRight,
  Zap
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <>
      {/* Hide body overflow to allow scrolling */}
      <style>{`
        body { overflow: auto; }
      `}</style>
      
      <div className="bg-background min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-6 py-4 md:px-12">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-primary">
              <BookOpen className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-semibold text-foreground">Vocabbit</span>
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="text-base px-4 py-2"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="gap-2 text-base px-6 py-2"
            >
              Sign Up
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <div className="px-6 py-12 md:px-12 md:py-24">
        <div className="mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="relative">
            {/* Background decorative elements */}
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/3 rounded-full blur-3xl"></div>
            </div>

            <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 mb-20">
              {/* Left Column - Compelling Copy */}
              <div className="space-y-8 flex flex-col justify-center lg:justify-start">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2">
                    <Zap className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">AI-Powered Learning</span>
                  </div>

                  <div className="space-y-2 lg:mt-16">
                    <h1 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl leading-tight">
                      Create Your Own
                      <span className="text-primary block">Vocabulary List</span>
                    </h1>
                    <div className="w-16 h-1 bg-primary rounded-full"></div>
                  </div>

                  <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-xl">
                    Add your vocabulary and instantly generate AI-powered fill-in-the-blank quizzes that adapt to your learning style.
                  </p>
                </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-base md:text-lg">Add Words Your Way</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Paste, import, or add one at a time</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-base md:text-lg">Generate Instant Quizzes</h3>
                    <p className="text-muted-foreground text-sm md:text-base">AI creates contextual fill-in-the-blank exercises</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-base md:text-lg">Track Your Progress</h3>
                    <p className="text-muted-foreground text-sm md:text-base">Monitor mastery levels and watch your growth</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Vocabulary Preview */}
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md relative">
                {/* Step 1 Indicator */}
                <div className="absolute -top-3 -right-3 md:-top-6 md:-right-6 z-10">
                  <div className="bg-primary rounded-lg px-2 py-1 shadow-md">
                    <span className="text-primary-foreground font-bold text-xs">STEP 1</span>
                  </div>
                </div>
                <Card className="border-2 border-primary/20 shadow-lg overflow-hidden">
                  <CardContent className="p-6 bg-background">
                    <div className="space-y-4">
                      {/* Header */}
                      <div>
                        <div className="flex items-center gap-2 mb-1 md:hidden">
                          <div className="bg-primary rounded px-1.5 py-0.5">
                            <span className="text-primary-foreground font-bold text-xs">STEP 1</span>
                          </div>
                          <h3 className="font-semibold text-base">Your Vocabulary List</h3>
                        </div>
                        <h3 className="font-semibold text-lg mb-1 hidden md:block">Your Vocabulary List</h3>
                        <p className="text-sm text-muted-foreground">5 words added this week</p>
                      </div>

                      {/* Word List Preview */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-3 bg-background rounded-lg border">
                          <div className="flex-1">
                            <p className="font-medium text-base">Experiment</p>
                            <p className="text-sm text-muted-foreground">scientific procedure to test a hypothesis</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-background rounded-lg border">
                          <div className="flex-1">
                            <p className="font-medium text-base">Hypothesis</p>
                            <p className="text-sm text-muted-foreground">proposed explanation for investigation</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-background rounded-lg border">
                          <div className="flex-1">
                            <p className="font-medium text-base">Conclusive</p>
                            <p className="text-sm text-muted-foreground">serving to prove or settle finally</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-3 bg-background rounded-lg border">
                          <div className="flex-1">
                            <p className="font-medium text-base">Theory</p>
                            <p className="text-sm text-muted-foreground">system of ideas to explain something</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
          </div>

          <div className="mt-20 space-y-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-2 md:hidden">
                <div className="bg-primary rounded px-1.5 py-0.5">
                  <span className="text-primary-foreground font-bold text-xs">STEP 2</span>
                </div>
                <h2 className="text-2xl font-bold">See It In Action</h2>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 hidden md:block">See It In Action</h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">Try our interactive quiz</p>
            </div>
            <div className="flex justify-center">
              <div className="w-full max-w-3xl relative">
                {/* Step 2 Indicator */}
                <div className="absolute -top-3 -left-3 md:-top-6 md:-left-6 z-10">
                  <div className="bg-primary rounded-lg px-2 py-1 shadow-md">
                    <span className="text-primary-foreground font-bold text-xs">STEP 2</span>
                  </div>
                </div>
                <Card className="border-2 border-primary/20 shadow-lg overflow-hidden">
                  <CardContent className="p-6 md:p-8 bg-background">
                    <FillInTheBlankDemo
                      paragraph={fillInTheBlankDemoData.paragraph}
                      answers={fillInTheBlankDemoData.answers}
                      words={fillInTheBlankDemoData.words}
                      title="Interactive Quiz"
                      description="Fill in the blanks to see how learning works"
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>

          {/* Start Your Journey CTA */}
          <div className="mt-20 py-12 bg-background rounded-2xl border border-primary/20 shadow-lg relative">
            <div className="text-center space-y-6">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  Build Your Vocabulary
                </h2>
                <p className="text-lg md:text-xl text-foreground max-w-2xl mx-auto">
                  Learn smarter with AI-powered quizzes tailored to your progress
                </p>
              </div>

              <div className="pt-4">
                <Button
                  onClick={() => navigate('/register')}
                  className="gap-3 text-lg px-8 py-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 bg-primary hover:bg-primary/90"
                  size="lg"
                >
                  Start Learning
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default LandingPage;
